import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useFeatureFlagStore from '../../store/featureFlagStore';

interface ProtectedRouteProps {
  element: React.ReactNode;
  requiredRole?: 'admin' | 'user' | 'group_admin';
  featureKey?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  element, 
  requiredRole,
  featureKey
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const { initializeWithUser, isFeatureEnabled, userPermissionsLoaded } = useFeatureFlagStore();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  
  // When the route is mounted, ensure feature flags are loaded
  useEffect(() => {
    const loadFlags = async () => {
      if (user) {
        // Use the more robust initialization method
        await initializeWithUser();
        console.log(`ProtectedRoute: Loaded feature flags for ${user.role}`);
      }
      setIsLoading(false);
    };
    
    loadFlags();
  }, [user, initializeWithUser]);
  
  // If still loading feature flags or user permissions haven't been loaded yet, show loading
  if (isLoading || (featureKey && !userPermissionsLoaded)) {
    console.log(`⏳ ProtectedRoute waiting for permissions:`, {
      isLoading,
      userPermissionsLoaded,
      featureKey,
      pathname: location.pathname
    });
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }
  
  // If user is not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  
  // If a specific role is required, check user's role
  if (requiredRole) {
    const hasAccess = 
      user.role === requiredRole || // User has exact required role
      (requiredRole === 'user' && (user.role === 'admin' || user.role === 'group_admin')) || // Admins and group admins can access user routes
      (requiredRole === 'group_admin' && user.role === 'admin') || // Admins can access group_admin routes
      (requiredRole === 'admin' && user.organization_id && !user.is_super_admin); // Organization admins can access admin routes
    
    if (!hasAccess) {
      console.log(`Access to ${location.pathname} denied: Required role ${requiredRole}, user has role ${user.role}, organization_id: ${user.organization_id}`);
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  // Check feature access if a feature key is specified
  if (featureKey) {
    const featureEnabled = isFeatureEnabled(featureKey);
    console.log(`🔍 ProtectedRoute feature check:`, {
      pathname: location.pathname,
      featureKey,
      featureEnabled,
      userId: user.id,
      userRole: user.role,
      isOrgAdmin: !!user.organization_id
    });
    
    if (!featureEnabled) {
      console.log(`❌ Access to ${location.pathname} denied: Feature ${featureKey} is disabled for user ${user.id}`);
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  // User is authenticated, has the required role, and feature is enabled
  return <>{element}</>;
};

export default ProtectedRoute; 